import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Target, PenLine, Camera, BarChart3, type LucideIcon } from "lucide-react";
import { SiFacebook, SiInstagram } from "react-icons/si";
import { FaLinkedinIn as SiLinkedin } from "react-icons/fa";
import agent1 from "@assets/Gemini_Generated_Image_jp7nmijp7nmijp7n (1)_1760809993862.png";
import agent2 from "@assets/Gemini_Generated_Image_vnvxzyvnvxzyvnvx (1)_1760810006394.png";
import agent3 from "@assets/Gemini_Generated_Image_3pwcq03pwcq03pwc_1760810036453.png";
import agent4 from "@assets/Gemini_Generated_Image_akydq1akydq1akyd_1760810088993.png";

interface PostContent {
  text: string;
  hashtags?: string[];
}

interface Comments {
  lina?: string | null;
  max?: string | null;
  emma?: string | null;
  alex?: string | null;
}

interface PostCardProps {
  platform: "instagram" | "facebook" | "linkedin";
  post: PostContent | null;
  comments: Comments;
  photoUrl?: string;
}

const platformConfig = {
  instagram: {
    name: "Instagram",
    icon: SiInstagram,
    color: "bg-gradient-to-r from-purple-500 to-pink-500",
    textColor: "text-white",
  },
  facebook: {
    name: "Facebook",
    icon: SiFacebook,
    color: "bg-blue-600",
    textColor: "text-white",
  },
  linkedin: {
    name: "LinkedIn",
    icon: SiLinkedin,
    color: "bg-blue-700",
    textColor: "text-white",
  },
};

const teamContributions: { name: string; role: string; icon: LucideIcon; image: string }[] = [
  { name: "Lina", role: "Strategie", icon: Target, image: agent1 },
  { name: "Max", role: "Text", icon: PenLine, image: agent4 },
  { name: "Emma", role: "Bildauswahl", icon: Camera, image: agent3 },
  { name: "Alex", role: "Timing", icon: BarChart3, image: agent2 },
];

export function PostCard({ platform, post, comments, photoUrl }: PostCardProps) {
  const [imageError, setImageError] = useState(false);
  
  if (!post || !post.text) return null;

  const config = platformConfig[platform];
  const Icon = config.icon;
  
  // Check if we should show the image
  const showImage = photoUrl && photoUrl.startsWith("http") && !imageError;

  // Get the comment for this post based on platform
  const getRelevantComment = () => {
    // Each platform gets a different team member's comment
    switch (platform) {
      case "instagram":
        return comments.emma ? { name: "Emma", comment: comments.emma, image: agent3 } : null;
      case "facebook":
        return comments.max ? { name: "Max", comment: comments.max, image: agent4 } : null;
      case "linkedin":
        return comments.lina ? { name: "Lina", comment: comments.lina, image: agent1 } : null;
      default:
        return null;
    }
  };

  const relevantComment = getRelevantComment();

  return (
    <Card className="overflow-hidden" data-testid={`post-card-${platform}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <Badge className={`${config.color} ${config.textColor} px-3 py-1`}>
            <Icon className="w-4 h-4 mr-2" />
            {config.name}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Photo if available and valid */}
        {showImage && (
          <div className="rounded-lg overflow-hidden bg-muted aspect-video">
            <img
              src={photoUrl}
              alt="Post visual"
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          </div>
        )}

        {/* Post text */}
        <div className="space-y-2">
          <p className="text-sm leading-relaxed whitespace-pre-wrap">
            {post.text}
          </p>

          {/* Hashtags */}
          {post.hashtags && post.hashtags.length > 0 && (
            <p className="text-sm text-primary">
              {post.hashtags.map((tag) => (tag.startsWith("#") ? tag : `#${tag}`)).join(" ")}
            </p>
          )}
        </div>

        {/* Team contributions */}
        <div className="border-t pt-4">
          <p className="text-xs text-muted-foreground mb-3">
            Erstellt vom A-Team:
          </p>
          <div className="flex flex-wrap gap-2">
            {teamContributions.map((member) => {
              const IconComponent = member.icon;
              return (
                <div
                  key={member.name}
                  className="flex items-center gap-1.5 text-xs bg-muted px-2 py-1 rounded-full"
                  data-testid={`badge-contribution-${member.name.toLowerCase()}`}
                >
                  <IconComponent className="w-3 h-3" />
                  <span className="font-medium">{member.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Personal comment from team member */}
        {relevantComment && (
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
            <div className="flex items-start gap-3">
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarImage src={relevantComment.image} alt={relevantComment.name} />
                <AvatarFallback>{relevantComment.name[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">{relevantComment.name} sagt:</p>
                <p className="text-sm text-muted-foreground italic">
                  "{relevantComment.comment}"
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
